'use client';

import { ShieldCheck, TrendingUp, DollarSign, Users } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';

export function MarketingStats() {
  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700">
      {/* Stats bar */}
      <div className="grid grid-cols-3 gap-4">
        <div className="flex flex-col items-center gap-1.5 rounded-xl border border-border bg-card p-4 sm:p-5">
          <span className="text-2xl font-bold text-foreground sm:text-3xl">2,400+</span>
          <span className="text-xs text-muted-foreground text-center leading-snug">Projects analyzed</span>
        </div>
        <div className="flex flex-col items-center gap-1.5 rounded-xl border border-border bg-card p-4 sm:p-5">
          <span className="text-2xl font-bold text-foreground sm:text-3xl">23%</span>
          <span className="text-xs text-muted-foreground text-center leading-snug">Avg. score improvement</span>
        </div>
        <div className="flex flex-col items-center gap-1.5 rounded-xl border border-border bg-card p-4 sm:p-5">
          <span className="text-2xl font-bold text-foreground sm:text-3xl">$4.2M</span>
          <span className="text-xs text-muted-foreground text-center leading-snug">Avg. savings per project</span>
        </div>
      </div>

      {/* Feature benefits */}
      <div className="space-y-4">
        <Card>
          <CardContent className="flex items-start gap-4 p-5">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-emerald-50">
              <ShieldCheck className="h-5 w-5 text-emerald-600" />
            </div>
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-foreground">Catch issues before they cost you</h3>
              <p className="mt-1 text-xs text-muted-foreground leading-relaxed">
                Poor schedule quality is the #1 predictor of cost overruns. Every point below an A-grade increases delay
                risk by 12%.
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="flex items-start gap-4 p-5">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-blue-50">
              <TrendingUp className="h-5 w-5 text-blue-600" />
            </div>
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-foreground">Continuous schedule health monitoring</h3>
              <p className="mt-1 text-xs text-muted-foreground leading-relaxed">
                Planera runs DCMA checks automatically with every schedule update, so your team never submits a schedule
                that doesn&apos;t meet the standard.
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="flex items-start gap-4 p-5">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-green-50">
              <DollarSign className="h-5 w-5 text-green-600" />
            </div>
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-foreground">Defend every dollar in your baseline</h3>
              <p className="mt-1 text-xs text-muted-foreground leading-relaxed">
                Construction firms using Planera recover an average of $4.2M per project by identifying logic gaps,
                float erosion, and constraint issues early.
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="flex items-start gap-4 p-5">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-purple-50">
              <Users className="h-5 w-5 text-purple-600" />
            </div>
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-foreground">Trusted by top ENR 400 contractors</h3>
              <p className="mt-1 text-xs text-muted-foreground leading-relaxed">
                From $50M tenant improvements to $2B infrastructure programs, Planera is the schedule quality platform
                teams rely on.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Customer testimonial */}
      <Card className="border-2 border-primary/20 bg-white">
        <CardContent className="p-6 space-y-4">
          <Badge variant="outline" className="text-xs font-semibold uppercase tracking-wider">
            Customer Story
          </Badge>
          <blockquote className="text-sm italic text-foreground leading-relaxed">
            &ldquo;After implementing Planera, our schedule quality scores went from a C average to consistent A grades
            across all active projects. We caught a critical path issue on a $180M hospital project that would have cost
            us 6 weeks and $3.2M in liquidated damages.&rdquo;
          </blockquote>
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-muted text-xs font-bold text-muted-foreground">
              MR
            </div>
            <div>
              <p className="text-sm font-semibold text-foreground">Michael Rivera</p>
              <p className="text-xs text-muted-foreground">Director of Project Controls, Turner & Townsend</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
